import { create } from 'zustand';
import type { PerformanceMetrics, RevenueBreakdown, TaxCalculations, NetProfit } from '../types';
import { useProductionStore } from './productionStore';
import { usePricingStore } from './pricingStore';
import { useExpenseStore } from './expenseStore';
import {
  calculateFCR,
  calculateSurvivalRate,
  calculateEPEF,
  findTargetFCR,
} from '../lib/calculations/performance';
import { FCR_SCALE_TABLE } from '../lib/utils/constants';
import {
  calculateMeatRevenue,
  calculateFeedBonus,
  calculateChickBonus,
  calculateEPEFPremium,
  calculateCoalSupport,
} from '../lib/calculations/revenue';
import { calculateVAT, calculateWitholdingTax } from '../lib/calculations/taxes';
import { calculateAnnualProjections } from '../lib/calculations/projections';

interface CalculationStore {
  metrics: PerformanceMetrics | null;
  revenue: RevenueBreakdown | null;
  taxes: TaxCalculations | null;
  profit: NetProfit | null;
  calculate: () => void;
  isValid: () => boolean;
}

export const useCalculationStore = create<CalculationStore>((set) => ({
  metrics: null,
  revenue: null,
  taxes: null,
  profit: null,

  isValid: () => {
    const productionData = useProductionStore.getState().data;
    
    // Check required production data
    if (productionData.chickCount <= 0) {
      return false;
    }
    
    // Check cycle days - must have either manual days OR both dates
    const hasManualDays = productionData.cycleDaysManual > 0;
    const hasBothDates = productionData.placementDate && productionData.harvestDate;
    
    if (!hasManualDays && !hasBothDates) {
      return false;
    }
    
    // Check harvest data
    const totals = useProductionStore.getState().calculateHarvestTotals();
    if (totals.totalCount <= 0 || totals.totalWeight <= 0) {
      return false;
    }
    
    return true;
  },

  calculate: () => {
    const productionData = useProductionStore.getState().data;
    const pricingParams = usePricingStore.getState().parameters;
    const manualFCR = usePricingStore.getState().manualTargetFCR;

    // Calculate harvest totals
    const harvestTotals = useProductionStore.getState().calculateHarvestTotals();
    const { totalCount, totalWeight, averageWeight } = harvestTotals;

    // Calculate cycle days - use manual value if provided, otherwise calculate from dates
    let cycleDays = productionData.cycleDaysManual;
    
    if (!cycleDays && productionData.placementDate && productionData.harvestDate) {
      const placementDate = new Date(productionData.placementDate);
      const harvestDate = new Date(productionData.harvestDate);
      cycleDays = Math.ceil(
        (harvestDate.getTime() - placementDate.getTime()) / (1000 * 60 * 60 * 24)
      );
    }

    // Calculate performance metrics
    const actualFCR = calculateFCR(productionData.feedConsumed, totalWeight);
    const targetFCR = manualFCR !== null ? manualFCR : findTargetFCR(averageWeight, FCR_SCALE_TABLE);
    const fcrSource: 'table' | 'manual' = manualFCR !== null ? 'manual' : 'table';
    const survivalRate = calculateSurvivalRate(totalCount, productionData.chickCount);
    const epef = calculateEPEF(averageWeight, survivalRate, cycleDays, actualFCR);

    const metrics: PerformanceMetrics = {
      fcr: actualFCR,
      targetFCR,
      fcrSource,
      survivalRate,
      epef,
      cycleDays,
    };

    // Calculate revenue components
    const meatRevenue = calculateMeatRevenue(totalWeight, pricingParams.meatPricePerKg);
    const feedBonus = calculateFeedBonus(
      targetFCR,
      actualFCR,
      totalWeight,
      pricingParams.feedBonusRate,
      pricingParams.feedPenaltyRate
    );
    const chickBonus = calculateChickBonus(
      productionData.chickCount,
      totalCount,
      pricingParams.chickMortalityLimit,
      pricingParams.chickBonusRate,
      pricingParams.chickPenaltyRate
    );
    const epefPremium = calculateEPEFPremium(
      epef,
      [pricingParams.epefLimit1, pricingParams.epefLimit2, pricingParams.epefLimit3],
      [pricingParams.epefPrice1, pricingParams.epefPrice2, pricingParams.epefPrice3],
      totalWeight
    );
    const coalSupport = calculateCoalSupport(totalWeight, pricingParams.coalSupportPerTon);
    const grossRevenue = meatRevenue + feedBonus + chickBonus + epefPremium + coalSupport;

    const revenue: RevenueBreakdown = {
      meatRevenue,
      feedBonus,
      chickBonus,
      epefPremium,
      coalSupport,
      grossRevenue,
    };

    // Calculate taxes - use dynamic VAT rate from expense store
    const expenseStore = useExpenseStore.getState();
    const vatRate = expenseStore.defaultVatRate / 100; // Convert percentage to decimal
    const { vatAmount, vatInclusiveRevenue } = calculateVAT(grossRevenue, vatRate);
    const { witholdingAmount, netRevenue } = calculateWitholdingTax(grossRevenue, 0.04);
    
    const pricePerKgVatExclusive = totalWeight > 0 ? grossRevenue / totalWeight : 0;
    const pricePerKgVatInclusive = totalWeight > 0 ? vatInclusiveRevenue / totalWeight : 0;
    const pricePerKgNetAfterWitholding = totalWeight > 0 ? netRevenue / totalWeight : 0;

    const revenuePerBird = totalCount > 0 ? grossRevenue / totalCount : 0;
    const netRevenuePerBird = totalCount > 0 ? netRevenue / totalCount : 0;

    const taxes: TaxCalculations = {
      vatAmount,
      vatInclusiveTotal: vatInclusiveRevenue,
      pricePerKg: pricePerKgVatExclusive,
      pricePerKgWithVAT: pricePerKgVatInclusive,
      revenuePerBird,
      withholdingTaxAmount: witholdingAmount,
      netAfterWithholding: netRevenue,
      netPricePerKg: pricePerKgNetAfterWitholding,
      netRevenuePerBird,
    };

    // Calculate net profit and projections
    const totalExpenses = useExpenseStore.getState().calculateTotalExpenses();
    const netProfit = grossRevenue - totalExpenses;
    const projections = calculateAnnualProjections(netProfit);

    const profit: NetProfit = {
      grossRevenue,
      totalExpenses,
      netProfit,
      annualProjections: [
        { cyclesPerYear: 5, annualRevenue: projections.cycles5.annual, monthlyAverage: projections.cycles5.monthly },
        { cyclesPerYear: 5.5, annualRevenue: projections.cycles55.annual, monthlyAverage: projections.cycles55.monthly },
        { cyclesPerYear: 6, annualRevenue: projections.cycles6.annual, monthlyAverage: projections.cycles6.monthly },
      ],
    };

    set({ metrics, revenue, taxes, profit });
  },
}));
